import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

export function isProCourseCertificateEligible(course: {
  completed_at?: string | null;
  review?: unknown;
  passed_at?: string | null;
}): boolean {
  return Boolean(course.completed_at && course.review && course.passed_at);
}

export function buildCertificatePdfFilename(
  courseId: number,
  slug?: string
): string {
  const safe =
    typeof slug === 'string' && slug.trim().length > 0
      ? slug.trim().replace(/[^a-zA-Z0-9-_]+/g, '-')
      : `corso-${courseId}`;
  return `certificato-${safe}.pdf`;
}

/**
 * Scales image dimensions (px) to fit inside a PDF page (mm), preserving aspect ratio.
 */
export function fitRasterImageToPdfDimensions(
  canvasWidth: number,
  canvasHeight: number,
  pageWidthMm: number,
  pageHeightMm: number
): { widthMm: number; heightMm: number } {
  if (canvasWidth <= 0 || canvasHeight <= 0) {
    return { widthMm: 0, heightMm: 0 };
  }
  const aspect = canvasWidth / canvasHeight;
  let widthMm = pageWidthMm;
  let heightMm = widthMm / aspect;
  if (heightMm > pageHeightMm) {
    heightMm = pageHeightMm;
    widthMm = heightMm * aspect;
  }
  return { widthMm, heightMm };
}

export async function exportElementAsRasterPdf(
  element: HTMLElement,
  filename: string
): Promise<void> {
  if (typeof document !== 'undefined' && document.fonts?.ready) {
    await document.fonts.ready;
  }
  const canvas = await html2canvas(element, {
    backgroundColor: '#ffffff',
    scale: 2,
    useCORS: true,
    onclone: (_clonedDoc, clonedRoot) => {
      const cert = clonedRoot.querySelector('[data-certificate-export-root]');
      if (!(cert instanceof HTMLElement)) return;
      cert.style.setProperty('-webkit-print-color-adjust', 'exact');
      cert.style.setProperty('print-color-adjust', 'exact');
      cert.querySelectorAll('.certificate-print-color-exact').forEach(node => {
        if (node instanceof HTMLElement) {
          node.style.setProperty('-webkit-print-color-adjust', 'exact');
          node.style.setProperty('print-color-adjust', 'exact');
        }
      });
    },
  });

  const imgData = canvas.toDataURL('image/png');
  const orientation = canvas.width >= canvas.height ? 'landscape' : 'portrait';
  const pdf = new jsPDF({
    orientation,
    unit: 'mm',
    format: 'a4',
  });

  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const { widthMm, heightMm } = fitRasterImageToPdfDimensions(
    canvas.width,
    canvas.height,
    pageW,
    pageH
  );
  const x = (pageW - widthMm) / 2;
  const y = (pageH - heightMm) / 2;
  pdf.addImage(imgData, 'PNG', x, y, widthMm, heightMm);
  pdf.save(filename);
}
